// A layered WebAudio loop. Procedural, so it is IP-clean and weighs nothing.
//
// ~100 BPM, D major, four bars: D – Bm – G – A. Five layers arrive one at a
// time as homes light up: pad → bass → bells → shaker → melody. Everything is
// scheduled a quarter-second ahead on a timer, the standard "tale of two
// clocks" pattern, so a dropped frame never drops a note.
//
// Nothing here throws. A device with no AudioContext, or one that refuses to
// resume, gets silence and the game carries on.

const BPM = 100;
const BEAT = 60 / BPM;
const STEP = BEAT / 4;               // a sixteenth
const STEPS_PER_BAR = 16;
const BARS = 4;
const LOOP = STEPS_PER_BAR * BARS;
const LOOKAHEAD_MS = 100;
const SCHEDULE_AHEAD = 0.28;

// MIDI note numbers. D major pentatonic for the melody so nothing can clash.
const CHORDS = [
  [62, 66, 69],   // D
  [59, 62, 66],   // Bm
  [55, 59, 62],   // G
  [57, 61, 64],   // A
];
const BASS = [38, 35, 31, 33];
const MELODY = [
  // step, midi, length in steps — one phrase over four bars, gentle and rising
  [0, 74, 2], [2, 76, 2], [4, 78, 4], [8, 81, 2], [10, 78, 2], [12, 76, 4],
  [16, 74, 2], [18, 71, 2], [20, 74, 4], [24, 76, 2], [26, 74, 2], [28, 71, 4],
  [32, 67, 2], [34, 71, 2], [36, 74, 4], [40, 76, 2], [42, 74, 2], [44, 71, 4],
  [48, 69, 2], [50, 73, 2], [52, 76, 4], [56, 78, 4], [60, 81, 4],
];

const hz = (midi) => 440 * 2 ** ((midi - 69) / 12);

let ctx = null;
let master = null;
let noiseBuffer = null;
let timer = null;
let nextStepTime = 0;
let step = 0;
let layers = 0;
let enabled = true;
let running = false;
let gestureHooked = false;

function context() {
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = 0;
    // A gentle low-pass keeps the synths soft rather than buzzy.
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 5200;
    master.connect(lp).connect(ctx.destination);
    hookGesture();
  } catch {
    ctx = null;
  }
  return ctx;
}

/** iOS keeps a context suspended until a gesture; resume on the first one. */
function hookGesture() {
  if (gestureHooked || typeof document === 'undefined') return;
  gestureHooked = true;
  const resume = () => { try { ctx?.resume?.(); } catch { /* ignore */ } };
  document.addEventListener('pointerdown', resume, { passive: true });
  document.addEventListener('keydown', resume, { passive: true });
}

function noise() {
  if (noiseBuffer || !ctx) return noiseBuffer;
  const len = Math.floor(ctx.sampleRate * 0.25);
  noiseBuffer = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = noiseBuffer.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return noiseBuffer;
}

function tone(freq, at, dur, { type = 'sine', gain = 0.08, attack = 0.01, release = 0.1, detune = 0 } = {}) {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  osc.detune.value = detune;
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(gain, at + attack);
  g.gain.setValueAtTime(gain, Math.max(at + attack, at + dur - release));
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur + 0.02);
  osc.connect(g).connect(master);
  osc.start(at);
  osc.stop(at + dur + 0.05);
}

function bell(freq, at) {
  tone(freq, at, 0.9, { gain: 0.07, attack: 0.004, release: 0.7 });
  tone(freq * 2.01, at, 0.5, { gain: 0.025, attack: 0.004, release: 0.4 });
  tone(freq * 3.0, at, 0.25, { gain: 0.012, attack: 0.004, release: 0.2 });
}

function shaker(at, accent) {
  const src = ctx.createBufferSource();
  src.buffer = noise();
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 6200;
  bp.Q.value = 1.2;
  const g = ctx.createGain();
  const peak = accent ? 0.05 : 0.03;
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, at + 0.09);
  src.connect(bp).connect(g).connect(master);
  src.start(at);
  src.stop(at + 0.12);
}

function scheduleStep(s, at) {
  const bar = Math.floor(s / STEPS_PER_BAR) % BARS;
  const inBar = s % STEPS_PER_BAR;
  const chord = CHORDS[bar];

  // 1 · pad: the chord, swelling at the top of each bar.
  if (layers >= 1 && inBar === 0) {
    for (let i = 0; i < chord.length; i++) {
      tone(hz(chord[i]), at, BEAT * 4, { type: 'triangle', gain: 0.035, attack: 0.5, release: 1.2, detune: i * 3 - 3 });
      tone(hz(chord[i]) / 2, at, BEAT * 4, { type: 'sine', gain: 0.02, attack: 0.6, release: 1.2 });
    }
  }
  // 2 · bass: root on 1 and 3, a lift on the "and" of 2.
  if (layers >= 2) {
    if (inBar === 0 || inBar === 8) tone(hz(BASS[bar]), at, BEAT * 0.9, { type: 'triangle', gain: 0.11, attack: 0.01, release: 0.2 });
    if (inBar === 6) tone(hz(BASS[bar] + 7), at, BEAT * 0.45, { type: 'triangle', gain: 0.07, attack: 0.01, release: 0.12 });
  }
  // 3 · bells: chord tones arpeggiated on eighths, an octave up.
  if (layers >= 3 && inBar % 2 === 0) {
    const idx = (inBar / 2) % chord.length;
    const octave = inBar % 8 === 6 ? 24 : 12;
    bell(hz(chord[idx] + octave), at);
  }
  // 4 · shaker: off-beat eighths, accent on the backbeat.
  if (layers >= 4 && inBar % 2 === 0) shaker(at, inBar % 8 === 4);
  // 5 · melody.
  if (layers >= 5) {
    for (const [ms, midi, len] of MELODY) {
      if (ms === s) tone(hz(midi), at, STEP * len * 0.95, { type: 'triangle', gain: 0.075, attack: 0.02, release: 0.15 });
    }
  }
}

function tick() {
  if (!ctx || !running) return;
  try {
    while (nextStepTime < ctx.currentTime + SCHEDULE_AHEAD) {
      scheduleStep(step, nextStepTime);
      nextStepTime += STEP;
      step = (step + 1) % LOOP;
    }
  } catch { /* a scheduling error must never stop the game */ }
}

/** Begin the loop with the pad layer. Safe to call twice. */
export function start() {
  try {
    const ac = context();
    if (!ac) return;
    if (ac.state === 'suspended') ac.resume().catch(() => {});
    layers = Math.max(layers, 1);
    if (running) return;
    running = true;
    step = 0;
    nextStepTime = ac.currentTime + 0.05;
    master.gain.cancelScheduledValues(ac.currentTime);
    master.gain.setValueAtTime(0.0001, ac.currentTime);
    master.gain.exponentialRampToValueAtTime(enabled ? 1 : 0.0001, ac.currentTime + 0.8);
    timer = setInterval(tick, LOOKAHEAD_MS);
    tick();
  } catch { /* silence is fine */ }
}

/** One more instrument, up to five. */
export function addLayer() {
  layers = Math.min(5, layers + 1);
  return layers;
}

export function layerCount() { return layers; }

/** Fade out and stop scheduling. */
export function stop() {
  try {
    running = false;
    layers = 0;
    if (timer) { clearInterval(timer); timer = null; }
    if (ctx && master) {
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(Math.max(master.gain.value, 0.0001), t);
      master.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
    }
  } catch { /* ignore */ }
}

/** The Music toggle. Ducks to silence rather than tearing the graph down. */
export function setEnabled(on) {
  enabled = !!on;
  try {
    if (!ctx || !master) return;
    const t = ctx.currentTime;
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(Math.max(master.gain.value, 0.0001), t);
    master.gain.exponentialRampToValueAtTime(enabled && running ? 1 : 0.0001, t + 0.3);
  } catch { /* ignore */ }
}

export function isRunning() { return running; }
